import {Imports, IEditorElement, IEditorShape, IEditorText } from './base/editor.interfaces';
import {IEditorBackground } from "@/customizer/interfaces";
import {ICustomizerEditorModel} from "./editor.interface-customizer";
import {EditorTranslator} from "./base/translator";
import cloneDeep from "lodash.clonedeep";
import {fabric} from "fabric";


const globalImports: Imports = typeof global !== 'undefined' ? global as any : window as any;

// @ts-ignore
const SF: StorefrontEditorSF = globalImports.SF || {};
SF.Util = SF.Util || {} as any;

const Model = globalImports.SF.Model;
const Editor = globalImports.SF.Editor;

class CustomizerEditorTranslator extends EditorTranslator {
    constructor(public saveTextPreview: boolean, public extraPropertiesToInclude: string[]) {
        super(saveTextPreview, extraPropertiesToInclude);
    }

    public toElement(shape: fabric.Object): IEditorElement {
        const propertiesToInclude: any[] = ['fixed', 'order', 'customerEditable', 'pattern'];
        if (this.extraPropertiesToInclude.length > 0) {
            propertiesToInclude.push(...this.extraPropertiesToInclude);
        }
        const object = shape.toObject(propertiesToInclude);

        let element: any;

        const shapeAssetTypes = Object.keys(SF.Model.ShapeAssetTypes).map((key: string): string => {
            return SF.Model.ShapeAssetTypes[key];
        });

        // noinspection SuspiciousInstanceOfGuard
        if (shape instanceof Editor.Text) {
            const styles = object.styles;

            // generate a root styles object
            const rootStyles = {
                fill: object.fill,
                fontFamily: object.fontFamily,
                fontSize: object.fontSize,
                fontStyle: object.fontStyle,
                fontWeight: object.fontWeight,
                textAlign: object.textAlign,
                backgroundColor: object.backgroundColor,
                underline: object.underline,
                overline: object.overline,
                linethrough: object.linethrough,
                autoFontSize: (shape as IEditorText).autoFontSize,
                textDecoration: (shape as IEditorText).textDecoration,
                textTransform: (shape as IEditorText).textTransform,
                maxCharacters: (shape as IEditorText).maxCharacters,
                lockFontColor: (shape as IEditorText).lockFontColor,
                lockFontSize: (shape as IEditorText).lockFontSize,
                lockTextAlign: (shape as IEditorText).lockTextAlign,
                lockTextStyle: (shape as IEditorText).lockTextStyle,
            };

            element = new (Model as ICustomizerEditorModel).TextElement(object.text, rootStyles, styles, undefined, (shape as any).edited);
            if (this.saveTextPreview) {
                this.getPreviewImageFromTextAsBase64(shape).then(dataUrl => {
                    element.preview_image = dataUrl;
                });
            }
            // If shape.linkEdited is set, we have edited the text by the link.  Set edited to true.
            element.edited = (shape as any).edited || (shape as any).linkEdited;
            element.addedByUser = (shape as any).addedByUser;
            element.textBeforeEdit = (shape as any).textBeforeEdit;
            element.placeHolderTranslations = (shape as any).placeHolderTranslations;
            element.originalHeight = (shape as any).originalHeight;
            element.linkedZoneId = (shape as any).linkedZoneId;
            element.textZoneId = (shape as any).textZoneId;

        } else if (shape instanceof Editor.Clipart) {
            element = new Model.ClipartElement(object.image || null);
        } else if (shape instanceof Editor.PhotoGroup) {
            const image = object.photo || null;
            const imagePosition = fabric.util.object.clone(object.imagePosition);

            // noinspection SuspiciousInstanceOfGuard
            if (shape instanceof Editor.Background) {
                element = new Model.BackgroundElement(image, imagePosition);
                element.onSpine = (shape as IEditorBackground).onSpine;
            } else if ((shape as any) instanceof Editor.Foreground) {
                element = new Model.ForegroundElement(image, imagePosition);
            } else {
                element = new Model.PhotoElement(image, imagePosition, object.frame || null);
            }
        } else if (shape.type == 'group') { // if it is a group
            element = new Model.GroupElement(object, (shape as any)._objects, (shape as any).canvas);
            if ((shape as any).uid) {
                element.uid = (shape as any).uid;
            }
            return element;
        } else if (shape.type !== undefined && shapeAssetTypes.indexOf(shape.type) >= 0) { // if it is a shape asset
            element = object;
            element.uid = (shape as any).uid;
            element.mainType = (shape as any).mainType;
            element.x = object.left;
            element.y = object.top;
            element.width = object.width;
            element.height = object.height;
            // noinspection PointlessBooleanExpressionJS
            element.editable = !!(shape.selectable || (shape as any).editableSave);
            element.locked = object.locked;
            element.fixed = object.fixed;
            element.isLetterBoxed = object.isLetterBoxed;
            element.pattern = object.pattern;
            element.rotation = object.angle;
            element.opacity = object.opacity;
            element.effects = (shape as any).effects ? fabric.util.object.clone((shape as any).effects) : {};
            element.order = object.order;
            element.generateUid = (): string => {
                return this.generateUid();
            };
            return element;
        }

        if (element) {
            // default options
            element.uid = (shape as any).uid;
            element.x = object.left;
            element.y = object.top;
            element.width = object.width;
            element.height = object.height;
            // noinspection PointlessBooleanExpressionJS
            element.editable = !!(shape.selectable || (shape as any).editableSave);
            element.locked = object.locked;
            element.fixed = object.fixed;
            element.isLetterBoxed = object.isLetterBoxed;
            element.pattern = object.pattern;
            element.scaleX = object.scaleX;
            element.scaleY = object.scaleY;

            element.rotation = object.angle;
            element.opacity = object.opacity;
            element.effects = object.effects ? cloneDeep(object.effects) : {};

            // data for flash pass-through
            element.order = object.order;
            if (object.customerEditable !== undefined) {
                element.customerEditable = object.customerEditable;
            }

            this.extraPropertiesToInclude.forEach((extraPropertyToInclude: string) => {
                if (object[extraPropertyToInclude] !== undefined) {
                    Object.defineProperty(element, extraPropertyToInclude, {
                        value: object[extraPropertyToInclude],
                        writable: true,
                        enumerable: true,
                        configurable: true
                    });
                }
            });

            if ((shape as any).uniquePhotoId) {
                element.uniquePhotoId = (shape as any).uniquePhotoId;
            }
            if ((shape as any).userZoom) {
                element.userZoom = (shape as any).userZoom;
            }
        }
        return element as IEditorElement;
    }

    public toShape(element: IEditorElement|IEditorShape): IEditorShape {
        // we clone the element here
        // note that all the instanceof checks are against element, because the prototype isn't carried through
        let clonedElement: any;
        if (element instanceof SF.Model.ShapeAsset) {
            clonedElement = (element as any).jsonData;
            clonedElement.mainType = (element as any).mainType;
            clonedElement.uid = element.uid;
            clonedElement.locked = element.locked;
            clonedElement.fixed = element.fixed;
            clonedElement.isLetterBoxed = element.isLetterBoxed;
            clonedElement.pattern = element.pattern;
            clonedElement.editable = element.editable;
            clonedElement.editableSave = element.editableSave;
            clonedElement.order = element.order;
            clonedElement.generateUid = element.generateUid;
            clonedElement.effects = clonedElement.effects ? fabric.util.object.clone(clonedElement.effects) : {};
        } else {
            clonedElement = element;
        }

        // TODO: Create type for options
        const options: any = {
            uid: clonedElement.uid,
            left: clonedElement.x,
            top: clonedElement.y,
            width: clonedElement.width,
            height: clonedElement.height,
            angle: clonedElement.rotation,
            opacity: clonedElement.opacity,
            effects: clonedElement.effects ? cloneDeep(clonedElement.effects) : {},
            locked: clonedElement.locked,
            fixed: clonedElement.fixed,
            editableSave: clonedElement.editableSave,
            isLetterBoxed: clonedElement.isLetterBoxed,
            pattern: clonedElement.pattern,

            selectable: clonedElement.editable,

            // data for flash pass-through
            order: clonedElement.order,
            uniquePhotoId: clonedElement.uniquePhotoId ? clonedElement.uniquePhotoId : undefined,
            customerEditable: clonedElement.customerEditable
        };

        if (clonedElement.scaleX !== undefined) {
            options.scaleX = clonedElement.scaleX;
        }
        if (clonedElement.scaleY !== undefined) {
            options.scaleY = clonedElement.scaleY;
        }

        // We don't want backgrounds selectable in the customizer...
        if (element instanceof SF.Model.BackgroundElement) {
            options.selectable = false;
        }

        this.extraPropertiesToInclude.forEach((extraPropertyToInclude: string) => {
            if (clonedElement[extraPropertyToInclude] !== undefined) {
                Object.defineProperty(options, extraPropertyToInclude, {
                    value: clonedElement[extraPropertyToInclude],
                    writable: true,
                    enumerable: true,
                    configurable: true
                });
            }
        });
        const effects = options.effects;

        if (effects) {
            if (effects.shadowAngle || effects.shadowOpacity) {
                options.shadow = this.buildShadow(effects.shadowColor, effects.shadowOpacity, effects.shadowBlur, effects.shadowDistance, effects.shadowAngle, true);
            } else if (effects.glowOpacity || effects.glowBlur) {
                options.shadow = this.buildGlow(effects.glowColor, effects.glowOpacity, effects.glowBlur, true);
            }
        }

        // if it's a text object, convert and return
        if (element instanceof Model.TextElement) {
            options.styles = clonedElement.styles;
            options.preview_image = clonedElement.preview_image;
            options.preview_file = clonedElement.preview_file;
            options.edited = clonedElement.edited;
            options.editable = clonedElement.editable;
            options.customerEditable = clonedElement.customerEditable;
            options.addedByUser = clonedElement.addedByUser;
            options.textBeforeEdit = clonedElement.textBeforeEdit;
            options.placeHolderTranslations = clonedElement.placeHolderTranslations;
            options.originalHeight = clonedElement.originalHeight;
            options.textZoneId = clonedElement.textZoneId;
            options.linkedZoneId = clonedElement.linkedZoneId;
            options.textZoneId = clonedElement.textZoneId;
            return new Editor.Text(clonedElement.text || '', globalImports.fabric.util.object.extend(options, clonedElement.rootStyle));
        }

        // if its a clipart, convert and return
        if (element instanceof Model.ClipartElement) {
            return new Editor.Clipart(clonedElement.image, options);
        }
        // if it's a shape asset
        if (element as any instanceof SF.Model.ShapeAsset) {
            return clonedElement;
        }
        const shapeAssetTypes = Object.keys(SF.Model.ShapeAssetTypes).map(
            function (key: string): string {
                return SF.Model.ShapeAssetTypes[key];
            });
        if ((element as any).type && shapeAssetTypes.indexOf((element as any).type) >= 0) {
            return clonedElement;
        }

        if (element as any instanceof Model.GroupElement) {
            options.canvas = clonedElement._canvas;
            const innerObjects: fabric.Object[] = [];
            for (let i = 0; i < clonedElement._objects.length; i++) {
                const innerObject = clonedElement._objects[i];
                // if it's a shape
                if (innerObject.mainType && innerObject.mainType == 'shapeAsset') {
                    const convertedShapeAsset = this.toShape(innerObject);
                    if (convertedShapeAsset instanceof fabric.Object) {
                        innerObjects.push(convertedShapeAsset);
                    } else {
                        fabric.util.enlivenObjects([convertedShapeAsset], (livenObjects: fabric.Object[]) => {
                            innerObjects.push(livenObjects[0]);
                        }, '');
                    }

                } else if (!innerObject.type) {
                    innerObjects.push(this.toShape(innerObject));
                } else {
                    innerObjects.push(innerObject);
                }
            }
            const groupShape = new Editor.Group(innerObjects, options) as any;
            groupShape.subTargetCheck = true;
            return groupShape;
        }

        // photos and backgrounds require image position and frame position
        options.imagePosition = this.getElementImagePosition(clonedElement);
        if ((element as any).userZoom) {
            options.userZoom = (element as any).userZoom;
        }

        if (element as any instanceof Model.PhotoElement) {
            this.setEmptyImageFillColor(options, clonedElement);
            return new Editor.PhotoGroup(clonedElement.image, clonedElement.frame, options);
        }

        if (element as any instanceof Model.ForegroundElement) {
            return new Editor.Foreground(clonedElement.image, options);
        }
        options.onSpine = clonedElement.onSpine;
        return new Editor.Background(clonedElement.image, options);
    }

}
SF.Util.Translator = new CustomizerEditorTranslator(false, ['freeObjectId']);